import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Folder, Link2 } from 'lucide-react';
import { useChatStore } from '../../store/chatStore';
import { useUIStore } from '../../store/uiStore';
import { useFolderStore } from '../../store/folderStore';
import { Thread, Message, Folder as FolderType, FolderContextOut } from '../../types';
import { apiService } from '../../services/api';
import MessageList from './MessageList';
import MessageComposer from './MessageComposer';
import AddContextModal from '../folders/AddContextModal';
import { getRateLimitFromError, dispatchRateLimitExceeded, isAuthError } from '../../utils/rateLimit';
import config from '../../config';

// Fallback title for threads without a title (makes it easy to debug by searching for this constant)
const THREAD_NO_TITLE_FALLBACK = 'Untitled Thread';

const ChatInterface: React.FC = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { selectedFolder } = useUIStore();
  const { folders } = useFolderStore();
  
  // Track which message IDs are currently streaming (for both assistant and system messages)
  const [streamingMessageIds, setStreamingMessageIds] = React.useState<Set<string>>(new Set());
  
  // Debug: Log when selectedFolder changes
  React.useEffect(() => {
  }, [selectedFolder]);
  const { 
    currentThread, 
    messages, 
    isLoadingMessages, 
    isSendingMessage,
    setCurrentThread,
    sendMessage
  } = useChatStore();

  React.useEffect(() => {
    if (threadId) {
      // Only set loading thread if we don't already have this thread loaded
      const currentThread = useChatStore.getState().currentThread;
      if (!currentThread || currentThread.id !== threadId) {
        // Load full thread details to get skill_id
        apiService.getThread(threadId).then(thread => {
          setCurrentThread(thread);
        }).catch(() => {
          // Fallback: create minimal thread object
          const thread: Thread = {
            id: threadId,
            title: 'Loading...', // Temporary title while loading
            created_at: new Date().toISOString(),
            folder_id: undefined,
            unread: false
          };
          setCurrentThread(thread);
        });
      }
    } else {
      setCurrentThread(null);
    }
  }, [threadId, setCurrentThread]);


  const ingestedUrlsRef = React.useRef<Set<string>>(new Set());

  const normalizeUrl = (rawUrl: string) => {
    try {
      const urlObj = new URL(rawUrl.trim());
      urlObj.hash = "";
      return urlObj.toString();
    } catch {
      return rawUrl.trim();
    }
  };

  const extractUrls = (text: string) => {
    const regex = /https?:\/\/[^\s<>"']+/gi;
    const matches = text.match(regex) || [];
    const urls = Array.from(new Set(matches.map(normalizeUrl)));
    console.log('🔍 [URL Extraction] extractUrls called', {
      text: text.substring(0, 200),
      textLength: text.length,
      rawMatches: matches,
      normalizedUrls: urls,
      urlCount: urls.length
    });
    return urls;
  };

  const uploadUrlWithProgress = async (targetUrl: string, threadHint?: string | null) => {
    const targetThreadId = threadHint ?? currentThread?.id ?? undefined;
    const tempMessageId = `temp-url-upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const createdAt = new Date().toISOString();
    const initialContent = `Ingesting URL: "${targetUrl}"...`;

    let activeMessageId = tempMessageId;
    let backendMessageId: string | null = null;
    let finalResult: any = null;
    let uploadError: Error | null = null;

    const initialMessage: Message = {
      id: tempMessageId,
      thread_id: targetThreadId ?? 'pending',
      role: 'system',
      content: initialContent,
      created_at: createdAt,
      citations: [],
    };

    // Mark this message as streaming
    setStreamingMessageIds((prev) => new Set(prev).add(tempMessageId));

    useChatStore.setState((state) => ({
      messages: [...state.messages, initialMessage],
    }));

    const updateMessage = (updates: Partial<Message>) => {
      useChatStore.setState((state) => ({
        messages: state.messages.map((msg) => {
          if (msg.id === activeMessageId) {
            return { ...msg, ...updates };
          }
          if (backendMessageId && msg.id === backendMessageId) {
            return { ...msg, ...updates };
          }
          return msg;
        }),
      }));
    };

    const replaceMessageId = (newId: string, threadFromServer?: string | null) => {
      if (!newId) return;
      if (backendMessageId === newId && activeMessageId === newId) return;

      const oldId = activeMessageId;
      backendMessageId = newId;
      const resolvedThreadId = threadFromServer ?? targetThreadId ?? currentThread?.id ?? 'pending';

      // Update streaming set when message ID changes
      setStreamingMessageIds((prev) => {
        const next = new Set(prev);
        if (next.has(oldId)) {
          next.delete(oldId);
          next.add(newId);
        }
        return next;
      });

      useChatStore.setState((state) => ({
        messages: state.messages.map((msg) => {
          if (msg.id === activeMessageId) {
            return {
              ...msg,
              id: newId,
              thread_id: resolvedThreadId,
            };
          }
          return msg;
        }),
      }));

      activeMessageId = newId;
    };

    try {
      const { apiService } = await import('../../services/api');
      // Use thread's folder_id when in a thread (so docs appear in /documents/{folderId}), else selectedFolder
      const folderIdForUpload = currentThread?.folder_id ?? selectedFolder ?? undefined;
      const stream = await apiService.uploadUrlDocumentStream(
        targetUrl,
        targetThreadId,
        folderIdForUpload
      );
      const reader = stream.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const event of events) {
          if (!event.trim() || !event.startsWith('data: ')) continue;
          const jsonStr = event.slice(6).trim();
          if (!jsonStr || jsonStr === '[DONE]') continue;

          try {
            const parsed = JSON.parse(jsonStr);
            const eventType = parsed.event;
            const data = parsed.data || {};

            if (eventType === 'status') {
              if (data.message_id) {
                replaceMessageId(data.message_id, data.thread_id);
              }
              if (typeof data.detail === 'string') {
                updateMessage({ content: data.detail });
              }
            } else if (eventType === 'completed') {
              if (data.message_id) {
                replaceMessageId(data.message_id, data.thread_id);
              }
              const finalMessage = data.final_message || `URL "${targetUrl}" ingested successfully.`;
              updateMessage({
                content: finalMessage,
                thread_id: data.thread_id ?? targetThreadId ?? currentThread?.id ?? 'pending',
                response_time: data.response_time, // Include upload time if available
              });
              // Stop streaming indicator
              setStreamingMessageIds((prev) => {
                const next = new Set(prev);
                next.delete(activeMessageId);
                if (data.message_id) next.delete(data.message_id);
                return next;
              });
              finalResult = data;
            } else if (eventType === 'error') {
              if (data.message_id) {
                replaceMessageId(data.message_id, data.thread_id);
              }
              const errorDetail = data.detail || `Failed to ingest URL "${targetUrl}".`;
              updateMessage({ content: errorDetail });
              // Stop streaming indicator
              setStreamingMessageIds((prev) => {
                const next = new Set(prev);
                next.delete(activeMessageId);
                if (data.message_id) next.delete(data.message_id);
                return next;
              });
              uploadError = new Error(errorDetail);
            }
          } catch (parseErr) {
            console.warn('⚠️ Failed to parse URL upload stream event:', parseErr);
          }
        }
      }

      reader.releaseLock();

      if (uploadError) {
        throw uploadError;
      }

      return finalResult;
    } catch (error: any) {
      // Stop streaming indicator on error
      setStreamingMessageIds((prev) => {
        const next = new Set(prev);
        next.delete(activeMessageId);
        if (backendMessageId) next.delete(backendMessageId);
        return next;
      });
      if (!uploadError && error instanceof Error) {
        updateMessage({ content: error.message });
      }
      throw error;
    }
  };

  const ingestUrlsIfNeeded = async (urls: string[], threadIdForIngest?: string | null) => {
    console.log('🌐 [URL Ingestion] ingestUrlsIfNeeded called', {
      urls,
      urlCount: urls.length,
      threadId: threadIdForIngest,
      folderId: selectedFolder,
      alreadyIngested: Array.from(ingestedUrlsRef.current)
    });

    if (!urls.length) {
      console.log('🌐 [URL Ingestion] No URLs to ingest, returning early');
      return true;
    }

    const { toast } = await import('react-hot-toast');

    for (const url of urls) {
      if (ingestedUrlsRef.current.has(url)) {
        console.log('🌐 [URL Ingestion] URL already ingested, skipping', { url });
        continue;
      }

      console.log('🌐 [URL Ingestion] Starting ingestion', {
        url,
        threadId: threadIdForIngest || undefined,
        folderId: selectedFolder || undefined
      });

      try {
        const result = await uploadUrlWithProgress(url, threadIdForIngest);
        console.log('✅ [URL Ingestion] Success', {
          url,
          result
        });

        ingestedUrlsRef.current.add(url);
      } catch (error) {
        console.error('❌ [URL Ingestion] Failed', {
          url,
          error,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined,
          errorType: error?.constructor?.name
        });

        const message = error instanceof Error ? error.message : 'Failed to ingest URL';
        toast.error(`${message} (${url})`);
        return false;
      }
    }

    console.log('✅ [URL Ingestion] All URLs processed successfully');
    return true;
  };

  const handleUploadUrl = async (targetUrl: string) => {
    const { toast } = await import('react-hot-toast');

    const normalized = normalizeUrl(targetUrl);
    const existingThreadId = currentThread?.id;

    try {
      const result = await uploadUrlWithProgress(normalized, existingThreadId);
      ingestedUrlsRef.current.add(normalized);
    } catch (error) {
      toast.error(`Failed to ingest URL: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleSendMessage = async (content: string, attachments?: File[]) => {
    if (!content.trim() && (!attachments || attachments.length === 0)) return;
    
    const { apiService } = await import('../../services/api');
    const { toast } = await import('react-hot-toast');
    
    
    // If we have a current thread, use it; otherwise create one before uploading
    let existingThreadId = currentThread?.id || undefined;

    // Step 0: Create thread FIRST if we have attachments but no thread
    // This ensures the system message is saved to the correct thread
    if (attachments && attachments.length > 0 && !existingThreadId) {
      try {
        // Create thread with a placeholder question (will be updated by actual message)
        const placeholderQuestion = content.trim() || 'Document upload';
        const newThread = await apiService.createThread(placeholderQuestion, selectedFolder || undefined);
        existingThreadId = newThread.id;
        
        // Update current thread in store so messages appear in the right place
        setCurrentThread(newThread);
        
        // Navigate to the new thread
        navigate(`/chat/${newThread.id}`);
        
        // Load messages for the new thread (will be empty initially)
        await useChatStore.getState().loadMessages(newThread.id);
      } catch (error) {
        console.error('Failed to create thread before upload:', error);
        toast.error('Failed to create thread. Please try again.');
        return;
      }
    }

    const uploadDocumentWithProgress = async (
      file: File, 
      threadHint?: string | null,
      onComplete?: (docId: string, threadId: string) => Promise<void>
    ) => {
      const targetThreadId = threadHint ?? existingThreadId ?? undefined;
      const tempMessageId = `temp-upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const createdAt = new Date().toISOString();
      const initialContent = `Uploading "${file.name}"...`;

      let activeMessageId = tempMessageId;
      let backendMessageId: string | null = null;
      let finalResult: any = null;
      let uploadError: Error | null = null;

      const initialMessage: Message = {
        id: tempMessageId,
        thread_id: targetThreadId ?? 'pending',
        role: 'system',
        content: initialContent,
        created_at: createdAt,
        citations: [],
      };

      // Mark this message as streaming
      setStreamingMessageIds((prev) => new Set(prev).add(tempMessageId));

      useChatStore.setState((state) => ({
        messages: [...state.messages, initialMessage],
      }));

      const updateMessage = (updates: Partial<Message>) => {
        useChatStore.setState((state) => ({
          messages: state.messages.map((msg) => {
            if (msg.id === activeMessageId) {
              return { ...msg, ...updates };
            }
            if (backendMessageId && msg.id === backendMessageId) {
              return { ...msg, ...updates };
            }
            return msg;
          }),
        }));
      };

      const replaceMessageId = (newId: string, threadFromServer?: string | null) => {
        if (!newId) return;
        if (backendMessageId === newId && activeMessageId === newId) return;

        const oldId = activeMessageId;
        backendMessageId = newId;
        const resolvedThreadId = threadFromServer ?? targetThreadId ?? existingThreadId ?? 'pending';

        // Update streaming set when message ID changes
        setStreamingMessageIds((prev) => {
          const next = new Set(prev);
          if (next.has(oldId)) {
            next.delete(oldId);
            next.add(newId);
          }
          return next;
        });

        useChatStore.setState((state) => ({
          messages: state.messages.map((msg) => {
            if (msg.id === activeMessageId) {
              return {
                ...msg,
                id: newId,
                thread_id: resolvedThreadId,
              };
            }
            return msg;
          }),
        }));

        activeMessageId = newId;
      };

      try {
        // Use thread's folder_id when in a thread (so docs appear in /documents/{folderId}), else selectedFolder
        const folderIdForUpload = currentThread?.folder_id ?? selectedFolder ?? undefined;
        const stream = await apiService.uploadDocumentStream(
          file,
          targetThreadId,
          folderIdForUpload
        );
        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const event of events) {
            if (!event.trim() || !event.startsWith('data: ')) continue;
            const jsonStr = event.slice(6).trim();
            if (!jsonStr || jsonStr === '[DONE]') continue;

            try {
              const parsed = JSON.parse(jsonStr);
              const eventType = parsed.event;
              const data = parsed.data || {};

              if (eventType === 'status') {
                if (data.message_id) {
                  replaceMessageId(data.message_id, data.thread_id);
                }
                if (typeof data.detail === 'string') {
                  updateMessage({ content: data.detail });
                }
              } else if (eventType === 'completed') {
                if (data.message_id) {
                  replaceMessageId(data.message_id, data.thread_id);
                }
                const finalMessage = data.final_message || `Document "${file.name}" uploaded successfully.`;
                updateMessage({
                  content: finalMessage,
                  thread_id: data.thread_id ?? targetThreadId ?? existingThreadId ?? 'pending',
                  response_time: data.response_time, // Include upload time if available
                });
                // Stop streaming indicator
                setStreamingMessageIds((prev) => {
                  const next = new Set(prev);
                  next.delete(activeMessageId);
                  if (data.message_id) next.delete(data.message_id);
                  return next;
                });
                // Store doc_id for status polling
                finalResult = {
                  ...data,
                  doc_id: data.doc_id || data.docId, // Support both naming conventions
                };
                
                // Auto-send user query if callback provided and document is ready
                // Check for both 'ready' status and 'completed' status (backend may send either)
                const isReady = (data.status === 'ready' || data.status === 'completed') && 
                                data.chunks !== undefined && data.chunks > 0;
                if (onComplete && data.doc_id && isReady && data.thread_id) {
                  try {
                    await onComplete(data.doc_id, data.thread_id);
                  } catch (queryErr) {
                    console.error('Failed to auto-send query after upload:', queryErr);
                    const { toast: toastImport } = await import('react-hot-toast');
                    toastImport.error('Document uploaded, but failed to send your query. Please try again.');
                  }
                }
              } else if (eventType === 'error') {
                if (data.message_id) {
                  replaceMessageId(data.message_id, data.thread_id);
                }
                const errorDetail = data.detail || `Failed to upload "${file.name}".`;
                updateMessage({ content: errorDetail });
                // Stop streaming indicator
                setStreamingMessageIds((prev) => {
                  const next = new Set(prev);
                  next.delete(activeMessageId);
                  if (data.message_id) next.delete(data.message_id);
                  return next;
                });
                uploadError = new Error(errorDetail);
              }
            } catch (parseErr) {
              console.warn('⚠️ Failed to parse upload stream event:', parseErr);
            }
          }
        }

        reader.releaseLock();

        if (uploadError) {
          throw uploadError;
        }

        return finalResult;
      } catch (error: any) {
        // Stop streaming indicator on error
        setStreamingMessageIds((prev) => {
          const next = new Set(prev);
          next.delete(activeMessageId);
          if (backendMessageId) next.delete(backendMessageId);
          return next;
        });
        if (!uploadError && error instanceof Error) {
          updateMessage({ content: error.message });
        }
        throw error;
      }
    };
    
    // Step 1: Upload documents first (if any) - this happens BEFORE thread creation
    // Store user query to auto-send after upload completes
    const userQueryToSend = content.trim();
    const uploadedDocIds: string[] = [];
    
    if (attachments && attachments.length > 0) {
      const uploadableMimeTypes = new Set([
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'text/markdown',
        'application/json',
        'text/csv',
        'text/tab-separated-values',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/html',
        'application/x-yaml',
        'text/yaml',
        'text/xml',
        'application/xml',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/rtf',
        'text/rtf',
        'application/vnd.oasis.opendocument.text',
        'application/vnd.oasis.opendocument.spreadsheet',
        'application/vnd.oasis.opendocument.presentation',
      ]);
      const uploadableExtensions = ['.pdf', '.docx', '.txt', '.md', '.markdown', '.json', '.jsonl', '.csv', '.tsv', '.xlsx', '.xlsm', '.html', '.htm', '.yaml', '.yml', '.xml', '.xsd', '.xsl', '.xslt', '.pptx', '.pptm', '.rtf', '.odt', '.ods', '.odp', '.ott', '.ots', '.otp'];

      const shouldUpload = (file: File) => {
        const normalizedType = file.type?.toLowerCase() ?? '';
        if (uploadableMimeTypes.has(normalizedType)) return true;
        const lowerName = file.name?.toLowerCase() ?? '';
        return uploadableExtensions.some((ext) => lowerName.endsWith(ext));
      };

      for (const file of attachments) {
        try {
          if (shouldUpload(file)) {
            // Pass callback to handle completion and auto-send query
            const result = await uploadDocumentWithProgress(
              file, 
              existingThreadId,
              userQueryToSend ? async (docId: string, threadId: string) => {
                // Auto-send user query when document is ready
                console.log('✅ Document ready, auto-sending user query:', userQueryToSend);
                await sendMessage(
                  userQueryToSend,
                  threadId,
                  selectedFolder,
                  false, // Don't create new thread
                  navigate
                );
              } : undefined
            );
            // Update thread_id if returned from upload (should match existingThreadId if we created it)
            if (result?.thread_id) {
              existingThreadId = result.thread_id;
              // Ensure currentThread is set to the thread where upload happened
              if (!currentThread || currentThread.id !== result.thread_id) {
                try {
                  const thread = await apiService.getThread(result.thread_id);
                  setCurrentThread(thread);
                } catch (err) {
                  console.warn('Could not fetch thread after upload:', err);
                }
              }
              // Reload messages to ensure system message appears
              await useChatStore.getState().loadMessages(result.thread_id);
            }
            
            // Store doc_id for tracking
            if (result?.doc_id) {
              uploadedDocIds.push(result.doc_id);
            }
            
            // No toast - document appears in UI automatically via system message
            // User can see upload progress and completion in the chat
          }
        } catch (error) {
          if (isAuthError(error)) return; // Interceptor already showed session expired and is redirecting to login
          const { isRateLimit, message: limitMsg } = getRateLimitFromError(error);
          if (isRateLimit) {
            dispatchRateLimitExceeded(limitMsg);
          } else {
            toast.error(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
      }
    }
    console.log('📤 [Message Send] Extracting URLs from message', {
      content: content.substring(0, 200),
      contentLength: content.length,
      attachmentsCount: attachments?.length || 0
    });

    const urlsInMessage = extractUrls(content);
    const urlsInAttachmentsMessage = extractUrls(
      attachments?.map((file) => file.name || '').join(' ') || ''
    );
    const allUrls = Array.from(new Set([...urlsInMessage, ...urlsInAttachmentsMessage]));

    console.log('📤 [Message Send] URLs extracted', {
      urlsInMessage,
      urlsInAttachmentsMessage,
      allUrls,
      uniqueUrlCount: allUrls.length
    });

    if (allUrls.length > 0) {
      console.log('📤 [Message Send] Starting URL ingestion before sending message');
      const ingestionOk = await ingestUrlsIfNeeded(allUrls, currentThread?.id);
      if (!ingestionOk) {
        console.error('❌ [Message Send] URL ingestion failed, aborting message send');
        return;
      }
      console.log('✅ [Message Send] URL ingestion completed, proceeding with message send');
    } else {
      console.log('📤 [Message Send] No URLs found, skipping ingestion');
    }

    // Step 2: Send message only if no files were uploaded
    // If files were uploaded with a query, the query is auto-sent when upload completes via onComplete callback
    // So we only send the query here if no files were uploaded
    if (content.trim() && (!attachments || attachments.length === 0)) {
      // No files uploaded - send query immediately
      await sendMessage(
        content,
        existingThreadId,
        selectedFolder,
        !existingThreadId, // startNew: true if no current thread
        navigate
      );
      
      // Thread title is automatically updated by the query endpoint based on the user's question
      // We don't need to update it with document title
    } else if (attachments && attachments.length > 0 && userQueryToSend) {
      // Files uploaded with query - query will be auto-sent when upload completes
      // The onComplete callback handles this, so we don't send here
      console.log('📤 Query will be sent automatically when document processing completes');
    } else {
      // User uploaded a document but didn't type anything
      // The system message about document upload is already handled by the backend
    }
  };

  // Helper function to find folder by ID recursively in the folder tree
  const findFolderById = (folderId: string, folderList: FolderType[]): FolderType | null => {
    for (const folder of folderList) {
      if (folder.id === folderId) {
        return folder;
      }
      if (folder.children && folder.children.length > 0) {
        const found = findFolderById(folderId, folder.children);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to build folder path hierarchy (from root to folder)
  const getFolderPath = (folder: FolderType, folderList: FolderType[]): FolderType[] => {
    const path: FolderType[] = [];
    
    // Helper to traverse up the parent chain and build path
    const buildPathUp = (currentFolder: FolderType | null): void => {
      if (!currentFolder) return;
      
      // If folder has a parent, find it first (build path bottom-up)
      if (currentFolder.parent_id) {
        const parent = findFolderById(currentFolder.parent_id, folderList);
        if (parent) {
          buildPathUp(parent); // Recursively build parent path first
        }
      }
      
      // Add current folder to path (after all parents)
      path.push(currentFolder);
    };
    
    buildPathUp(folder);
    return path;
  };

  // Get folder info - prioritize thread's folder_id over selectedFolder
  const getFolderInfo = (): FolderType | null => {
    // If we have a current thread, use its folder_id
    if (currentThread?.folder_id) {
      return findFolderById(currentThread.folder_id, folders);
    }
    // Otherwise, use selectedFolder (for new threads)
    if (selectedFolder) {
      return findFolderById(selectedFolder, folders);
    }
    return null;
  };

  const folderInfo = getFolderInfo();
  const folderPath = folderInfo ? getFolderPath(folderInfo, folders) : [];

  const targetFolderId = currentThread?.folder_id ?? selectedFolder ?? null;
  const [addContextOpen, setAddContextOpen] = React.useState(false);
  const [folderContextList, setFolderContextList] = React.useState<FolderContextOut[]>([]);

  React.useEffect(() => {
    if (!targetFolderId) {
      setFolderContextList([]);
      return;
    }
    apiService.getFolderContext(targetFolderId).then(setFolderContextList).catch(() => setFolderContextList([]));
  }, [targetFolderId]);

  // Show loading state
  if (isLoadingMessages) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="flex items-center justify-between py-2 px-3 sm:px-4 border-b border-gray-200 bg-white">
        <div className="flex-1 min-w-0">
          <h2 className="text-xs sm:text-sm font-semibold text-gray-800 truncate">
            {currentThread 
              ? (currentThread.title || THREAD_NO_TITLE_FALLBACK)
              : 'New Thread'}
          </h2>
          {folderInfo && folderPath.length > 0 && (
            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
              <span className="text-[11px] text-gray-500">In Folder</span>
              <Folder className="h-3 w-3 text-gray-500 flex-shrink-0" />
              {folderPath.map((folder, index) => (
                <React.Fragment key={folder.id}>
                  <span className="text-[11px] text-gray-600 font-medium truncate max-w-[100px] sm:max-w-none">{folder.name}</span>
                  {index < folderPath.length - 1 && (
                    <span className="text-[11px] text-gray-400 mx-0.5">→</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
        {targetFolderId && folderInfo && (
          <button
            type="button"
            onClick={() => setAddContextOpen(true)}
            className="flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded border border-primary-200 hover:border-primary-300 ml-2 flex-shrink-0"
            title="Add context (folders or data sources) to this folder"
          >
            <Link2 className="h-3 w-3" />
            <span className="hidden sm:inline">Add context</span>
          </button>
        )}
      </div>

      {targetFolderId && folderInfo && (
        <AddContextModal
          isOpen={addContextOpen}
          onClose={() => setAddContextOpen(false)}
          folderId={targetFolderId}
          folderName={folderInfo.name}
          existingContext={folderContextList}
          onAdded={() => apiService.getFolderContext(targetFolderId).then(setFolderContextList).catch(() => {})}
        />
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {messages.length === 0 && !currentThread ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 px-4">
            <div className="text-center">
              <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-2">Welcome to {config.appName}!</h3>
              <p className="text-xs sm:text-sm">Start a conversation by typing your question below.</p>
              <p className="text-xs mt-2 px-4">
                {folderInfo 
                  ? `Your message will create a new thread in "${folderInfo.name}" folder.`
                  : "Your message will create a new thread automatically."}
              </p>
            </div>
          </div>
        ) : (
          <MessageList 
            messages={messages} 
            isLoading={isLoadingMessages} 
            isStreaming={isSendingMessage}
            streamingMessageIds={streamingMessageIds}
          />
        )}
      </div>

      {/* Message Composer */}
      <MessageComposer 
        onSendMessage={handleSendMessage} 
        onUploadUrl={handleUploadUrl}
        disabled={isSendingMessage}
        placeholder={isSendingMessage ? "Sending..." : "Type your message..."} 
      />
    </div>
  );
};

export default ChatInterface;